import {ClickPlayer} from "../../shared/click-player";
import {bindBooleanControl, bindNumericControl, makeBpmValueEditable, wireHintPopovers} from "../../shared/controls";
import {
    ClickSoundId,
    DEFAULT_DOWNBEAT_SOUND,
    DEFAULT_SECONDARY_SOUND,
    SoundSection,
    toClickSoundId,
} from "../../shared/sounds";
import {Tool} from "../../shared/tool";
import {densityToMaxNoteCount} from "./density";
import {NoteSelection, MidiNote} from "./note-selection";
import {NoteSource} from "./note-source";
import {OscillatorNoteVoice} from "./note-voice";
import {PhraseCycle} from "./phrase-cycle";
import {PhraseGenerator} from "./phrase-generator";
import {PhraserPlayer} from "./phraser-player";
import {NoteRing} from "./note-ring";

interface PhraserSettings {
    bpm: number;
    beatsPerBar: number;
    countInBars: number;
    responseBars: number;
    hasClickTrack: boolean;
    density: number;
    granularity: number;
    tupletBias: number;
    downbeatSound: ClickSoundId;
    secondarySound: ClickSoundId;
}

/**
 * The single live config object the engine reads: PhraserPlayerConfig,
 * PhraseCycleConfig and PhraseGeneratorConfig share fields by reference, so
 * every control change applies at the next note, bar, or start boundary.
 */
interface PhraserLiveConfig {
    bpm: number;
    beatsPerBar: number;
    countInBars: number;
    hasClickTrack: boolean;
    responseBars: number;
    granularity: number;
    tupletBias: number;
    maxNoteCount: number;
}

/**
 * Phraser's composition module: owns its cards' control bindings, the
 * NoteSelection model, and the lazy audio/engine construction (AudioContext
 * created on first user gesture, shared between ClickPlayer and the note
 * voice). The UI stays dumb: all selection semantics live in the model.
 */

/**
 * Note name with register number (e.g. C4, F♯4); the pitch-class label plus
 * the register. Takes the label table from the caller's selection rather
 * than reading the model's private table (a throwaway `new NoteSelection()`
 * per call re-read localStorage for static data).
 */
function noteName(n: MidiNote, labels: string[]): string {
    const mod12 = ((n % 12) + 12) % 12;
    const register = Math.floor(n / 12) - 1;
    const label = labels[mod12];
    return label.includes('/') ? label.split('/')[0] + register : label + register;
}

class PhraserTool implements Tool {

    private readonly settings: PhraserSettings;
    private readonly selection = new NoteSelection();
    private readonly config: PhraserLiveConfig;

    private audioContext: AudioContext | undefined;
    private clickPlayer: ClickPlayer | undefined;
    private player: PhraserPlayer | undefined;
    private playing = false;
    private noteRing!: NoteRing;

    constructor() {
        this.settings = loadPhraserSettings();
        this.config = {
            bpm: this.settings.bpm,
            beatsPerBar: this.settings.beatsPerBar,
            countInBars: this.settings.countInBars,
            hasClickTrack: this.settings.hasClickTrack,
            responseBars: this.settings.responseBars,
            granularity: this.settings.granularity,
            tupletBias: this.settings.tupletBias,
            maxNoteCount: densityToMaxNoteCount(this.settings.density, this.settings.beatsPerBar),
        };
        this.initialise();
    }

    get isPlaying(): boolean {
        return this.playing;
    }

    /**
     * The engine and audio are created lazily, on the first user interaction
     * that needs audio (pressing play, or picking a sound in the Settings
     * sheet), so that the AudioContext is always created inside a user
     * gesture. Both players share one AudioContext: the scheduler's clock is
     * that context's currentTime.
     * @private
     */
    private getOrCreatePlayer(): PhraserPlayer {
        if (!this.player) {
            this.audioContext = new AudioContext();
            this.clickPlayer = new ClickPlayer(
                this.settings.downbeatSound, this.settings.secondarySound, this.audioContext);
            const noteVoice = new OscillatorNoteVoice(this.audioContext);
            const noteSource = new NoteSource(this.selection);
            const phraseGenerator = new PhraseGenerator(this.config, noteSource);
            const phraseCycle = new PhraseCycle(this.config, phraseGenerator);
            this.player = new PhraserPlayer(this.config, phraseCycle, this.clickPlayer, noteVoice);
        }
        return this.player;
    }

    private getOrCreateClickPlayer(): ClickPlayer {
        this.getOrCreatePlayer();
        return this.clickPlayer!;
    }

    private start() {
        this.playing = true;
        this.getOrCreatePlayer();
        this.clickPlayer!.resume()
            .then(() => this.player?.start());
    }

    stop() {
        this.playing = false;
        this.player?.stop();
        this.syncPlayControl();
    }

    onSoundSelected(section: SoundSection, sound: ClickSoundId) {
        const key = section === 'downbeat' ? 'downbeatSound' : 'secondarySound';
        this.settings[key] = sound;
        localStorage.setItem(key, sound);

        const audio = this.getOrCreateClickPlayer();
        if (section === 'downbeat') {
            audio.setPrimarySound(sound);
        } else {
            audio.setSecondarySound(sound);
        }
        if (!this.isPlaying) {
            void audio.preview(sound);
        }
    }

    // ---------------------------------------------------------------------
    // UI wiring
    // ---------------------------------------------------------------------

    private initialise() {
        const playCheckbox = document.getElementById('phraser-play-checkbox') as HTMLInputElement;
        if (playCheckbox) {
            playCheckbox.addEventListener('click', (event) => {
                const isPlaying = (event.target as HTMLInputElement).checked;
                if (isPlaying) {
                    this.start();
                } else {
                    this.stop();
                }
                this.syncPlayControl();
            });
        }

        const bindSetting = (id: string, key: 'bpm' | 'beatsPerBar' | 'countInBars' | 'responseBars'
            | 'density' | 'granularity' | 'tupletBias') =>
            bindNumericControl(id, () => this.settings[key], value => {
                localStorage.setItem(`phraser.${key}`, '' + value);
                this.settings[key] = value;
                this.applySetting(key);
            });

        bindSetting('phraser-bpm', 'bpm');
        makeBpmValueEditable('phraser-bpm-output', 'phraser-bpm-input', 'phraser-bpm');
        bindSetting('phraser-beatsPerBar', 'beatsPerBar');
        bindSetting('phraser-countInBars', 'countInBars');
        bindSetting('phraser-responseBars', 'responseBars');
        bindSetting('phraser-density', 'density');
        bindSetting('phraser-granularity', 'granularity');
        bindSetting('phraser-tuplets', 'tupletBias');
        bindBooleanControl('phraser-hasClickTrack', () => this.settings.hasClickTrack, isChecked => {
            localStorage.setItem('phraser.hasClickTrack', JSON.stringify(isChecked));
            this.settings.hasClickTrack = isChecked;
            this.config.hasClickTrack = isChecked;
        });

        this.initialiseNoteSelectionCard();
        wireHintPopovers(document.getElementById('tool-phraser')!);
        this.renderNoteSelectionCard();
        this.syncPlayControl();
    }

    /** Push a changed setting into the live config object the engine reads. */
    private applySetting(key: keyof PhraserSettings) {
        switch (key) {
            case 'bpm':
                this.config.bpm = this.settings.bpm;
                break;
            case 'beatsPerBar':
                this.config.beatsPerBar = this.settings.beatsPerBar;
                this.config.maxNoteCount = densityToMaxNoteCount(this.settings.density, this.settings.beatsPerBar);
                break;
            case 'density':
                this.config.maxNoteCount = densityToMaxNoteCount(this.settings.density, this.settings.beatsPerBar);
                break;
            case 'countInBars':
                this.config.countInBars = this.settings.countInBars;
                break;
            case 'responseBars':
                this.config.responseBars = this.settings.responseBars;
                break;
            case 'granularity':
                this.config.granularity = this.settings.granularity;
                break;
            case 'tupletBias':
                this.config.tupletBias = this.settings.tupletBias;
                break;
        }
    }

    // ---------------------------------------------------------------------
    // Note selection card
    // ---------------------------------------------------------------------

    private initialiseNoteSelectionCard() {
        // the ring sits in #phraser-notes; the ↻/↷ buttons live in the center hole
        const notes = document.getElementById('phraser-notes')!;
        this.noteRing = new NoteRing(
            notes,
            {
                onToggle: (pc: number) => {
                    this.selection.togglePitchClass(pc);
                    this.renderNoteSelectionCard();
                },
            },
            this.selection.pitchClassLabels(),
        );

        // Rotation is stateless: each click applies a bijective rotation to
        // the selection itself; the checkmarks orbit the clock face. The
        // buttons are never disabled (rotating an empty selection is a
        // harmless no-op).
        document.getElementById('phraser-rotate-anticlockwise')!.addEventListener('click', () => {
            this.selection.rotate(-1);
            this.renderNoteSelectionCard();
        });
        document.getElementById('phraser-rotate-clockwise')!.addEventListener('click', () => {
            this.selection.rotate(1);
            this.renderNoteSelectionCard();
        });

        const rangeLow = document.getElementById('phraser-range-low') as HTMLInputElement;
        rangeLow.addEventListener('input', () => {
            this.selection.setRangeLow(rangeLow.valueAsNumber);
            this.renderNoteSelectionCard();
        });
        const rangeHigh = document.getElementById('phraser-range-high') as HTMLInputElement;
        rangeHigh.addEventListener('input', () => {
            this.selection.setRangeHigh(rangeHigh.valueAsNumber);
            this.renderNoteSelectionCard();
        });

        // Thumb grabs only: the track itself is inert. On touch, a track
        // press is almost always a scroll gesture that happens to start on
        // the slider, and snapping a thumb to it would silently rewrite
        // the window (native single-thumb sliders get the same treatment
        // via guardRangeTrackPress in shared/controls). The container has
        // touch-action: pan-y, so vertical scrolls pass over the track
        // while horizontal thumb drags stay with the control.
        //
        // Thumb grabs take the native path, with one fix-up: when the
        // thumbs overlap (coincident or within a thumb width), hit-testing
        // hands the press to the input on top in z-order regardless of
        // which thumb the user aimed at, and the top input's native drag
        // clamps against the other thumb - the window cannot be widened by
        // dragging. Pointer capture does not retarget Chromium's native
        // slider drag, so a misrouted grab is dragged manually: the
        // intended thumb follows the pointer (mapped through the same
        // offset scale) until pointerup. Grabs that hit the right input
        // keep the fully native drag.
        const slider = document.querySelector<HTMLElement>('.range-slider')!;
        slider.addEventListener('pointerdown', (event) => {
            const rect = slider.getBoundingClientRect();
            // the inputs' thumb centers travel half a thumb width (12px)
            // in from the edges; map press positions onto the same scale
            const offsetAt = (clientX: number) => {
                const fraction = (clientX - rect.left - 12) / (rect.width - 24);
                return Math.round(Math.min(Math.max(fraction, 0), 1) * 36);
            };
            const offset = offsetAt(event.clientX);
            const [low, high] = this.selection.rangeOffsets();
            const onLowThumb = Math.abs(offset - low) <= 1;
            const onHighThumb = Math.abs(offset - high) <= 1;
            if (event.target instanceof HTMLInputElement && (onLowThumb || onHighThumb)) {
                // a thumb grab: which thumb did the user aim at? Compare the
                // raw (unrounded) press position against the thumbs' centers
                // - at coincidence the press side decides, so a grab just
                // right of the overlapped thumbs takes the high thumb, just
                // left takes the low one. A press dead center goes low.
                const rawOffset = (event.clientX - rect.left - 12) / (rect.width - 24) * 36;
                const intended = onLowThumb && (!onHighThumb || rawOffset - low <= high - rawOffset)
                    ? rangeLow : rangeHigh;
                if (event.target !== intended) {
                    // the wrong input is on top: suppress its native drag
                    // and move the intended thumb manually until pointerup.
                    // A preventDefault'ed press gets no implicit pointer
                    // capture, so the moves go to whatever is under the
                    // pointer - listening on window (bubble phase) catches
                    // them wherever they land
                    event.preventDefault();
                    intended.focus();
                    const setThumb = intended === rangeLow
                        ? (o: number) => this.selection.setRangeLow(o)
                        : (o: number) => this.selection.setRangeHigh(o);
                    const onMove = (moveEvent: PointerEvent) => {
                        setThumb(offsetAt(moveEvent.clientX));
                        this.renderNoteSelectionCard();
                    };
                    const onUp = () => {
                        window.removeEventListener('pointermove', onMove);
                        window.removeEventListener('pointerup', onUp);
                        window.removeEventListener('pointercancel', onUp);
                    };
                    window.addEventListener('pointermove', onMove);
                    window.addEventListener('pointerup', onUp);
                    window.addEventListener('pointercancel', onUp);
                }
                return;
            }
            // anything else is a track press: the track is inert, so the
            // gesture falls through to the page (scroll on touch, nothing
            // on mouse)
        });

        document.getElementById('phraser-octave-down')!.addEventListener('click', () => {
            this.selection.setOctave(this.selection.getOctave() - 1);
            this.renderNoteSelectionCard();
        });
        document.getElementById('phraser-octave-up')!.addEventListener('click', () => {
            this.selection.setOctave(this.selection.getOctave() + 1);
            this.renderNoteSelectionCard();
        });
    }

    /**
     * Re-render checkmarks (selected + dimmed states), thumb positions and
     * note-name readouts after every mutation: rotation moves checkmarks,
     * octave moves note names, range moves thumbs and can change which
     * selected classes are dimmed. Labels never move.
     */
    private renderNoteSelectionCard() {
        const sounded = this.selection.soundedNotes();
        const soundedClasses = new Set(sounded.map(n => ((n % 12) + 12) % 12));
        const selected = new Set<number>();
        for (let pc = 0; pc < 12; pc++) {
            if (this.selection.isSelected(pc)) {
                selected.add(pc);
            }
        }
        this.noteRing.update(selected, soundedClasses);

        const [lowOffset, highOffset] = this.selection.rangeOffsets();
        const rangeLow = document.getElementById('phraser-range-low') as HTMLInputElement;
        rangeLow.valueAsNumber = lowOffset;
        const labels = this.selection.pitchClassLabels();
        rangeLow.setAttribute('aria-valuetext', noteName(this.selection.rangeWindow()[0], labels));
        const rangeHigh = document.getElementById('phraser-range-high') as HTMLInputElement;
        rangeHigh.valueAsNumber = highOffset;
        rangeHigh.setAttribute('aria-valuetext', noteName(this.selection.rangeWindow()[1], labels));

        // the window fill between the thumbs: thumb centers travel half a
        // thumb width in from the track ends, so anchor the fill to the
        // thumb-center line, not the track ends (percentages alone would
        // misalign the fill edges with the thumbs)
        const fill = document.getElementById('phraser-range-fill')!;
        fill.style.left = `calc(0.75rem + (100% - 1.5rem) * ${lowOffset / 36})`;
        fill.style.right = `calc(0.75rem + (100% - 1.5rem) * ${(36 - highOffset) / 36})`;

        const [low, high] = this.selection.rangeWindow();
        document.getElementById('phraser-range-output')!.textContent =
            `${noteName(low, labels)} - ${noteName(high, labels)}`;
        document.getElementById('phraser-octave-output')!.textContent = '' + this.selection.getOctave();

        this.syncPlayControl();
    }

    /**
     * Keep the play toggle's visual/aria state in sync. The play control is
     * disabled whenever the sounded set is empty (strategy A); if the
     * selection is emptied mid-play, stop.
     */
    private syncPlayControl() {
        const playCheckbox = document.getElementById('phraser-play-checkbox') as HTMLInputElement;
        const playControl = playCheckbox?.closest('.play-control') as HTMLElement;
        if (!playCheckbox || !playControl) {
            return;
        }
        const canPlay = this.selection.soundedNotes().length > 0;
        if (!canPlay && this.playing) {
            this.stop();
            return;
        }
        playCheckbox.disabled = !canPlay;
        playControl.classList.toggle('disabled', !canPlay);
        playControl.setAttribute('aria-disabled', '' + !canPlay);
        playCheckbox.checked = this.playing;
        playControl.classList.toggle('playing', this.playing);
        playControl.setAttribute('aria-label', this.playing ? 'Stop' : 'Play');
    }
}

/** Load Phraser's settings from localStorage (phraser.-prefixed keys). */
function loadPhraserSettings(): PhraserSettings {
    return {
        bpm: +(localStorage.getItem('phraser.bpm') || 80),
        beatsPerBar: +(localStorage.getItem('phraser.beatsPerBar') || 4),
        countInBars: +(localStorage.getItem('phraser.countInBars') || 1),
        responseBars: +(localStorage.getItem('phraser.responseBars') || 1),
        hasClickTrack: (localStorage.getItem('phraser.hasClickTrack') !== 'false'),
        density: +(localStorage.getItem('phraser.density') || 25),
        granularity: +(localStorage.getItem('phraser.granularity') || 25),
        tupletBias: +(localStorage.getItem('phraser.tupletBias') || 1),
        downbeatSound: toClickSoundId(localStorage.getItem('downbeatSound'), DEFAULT_DOWNBEAT_SOUND),
        secondarySound: toClickSoundId(localStorage.getItem('secondarySound'), DEFAULT_SECONDARY_SOUND),
    };
}

export {PhraserTool};
